/**
 * Clause Library (Library → Clause Templates). Mirrors the Templateing
 * service's api/v1/clauselibrary/* contract.
 */

export const ClauseStatus = {
  Draft: 1,
  InReview: 2,
  Published: 3,
  Retired: 4,
} as const;
export type ClauseStatus = (typeof ClauseStatus)[keyof typeof ClauseStatus];

export const ClauseVersionStatus = {
  Draft: 1,
  InReview: 2,
  Published: 3,
  ChangesRequested: 4,
  Superseded: 5,
} as const;
export type ClauseVersionStatus =
  (typeof ClauseVersionStatus)[keyof typeof ClauseVersionStatus];

/** Playbook position: the preferred wording and the fallbacks a negotiator may concede to. */
export const ClausePosition = {
  Preferred: 1,
  Fallback: 2,
  WalkAway: 3,
} as const;
export type ClausePosition = (typeof ClausePosition)[keyof typeof ClausePosition];

export const ClauseRiskLevel = {
  Low: 1,
  Medium: 2,
  High: 3,
} as const;
export type ClauseRiskLevel = (typeof ClauseRiskLevel)[keyof typeof ClauseRiskLevel];

export const ClauseUsageTarget = {
  Contract: 1,
  ContractTemplate: 2,
} as const;
export type ClauseUsageTarget =
  (typeof ClauseUsageTarget)[keyof typeof ClauseUsageTarget];

export const ClauseApprovalDecision = {
  Approved: 1,
  ChangesRequested: 2,
} as const;
export type ClauseApprovalDecision =
  (typeof ClauseApprovalDecision)[keyof typeof ClauseApprovalDecision];

export interface PersonRef {
  id: string;
  name: string;
}

export interface ClauseAlternate {
  id?: string;
  position: typeof ClausePosition.Fallback | typeof ClausePosition.WalkAway;
  title: string;
  contentSfdt?: string | null;
  contentText: string;
  guidance?: string | null;
  riskLevel: ClauseRiskLevel;
  sortOrder: number;
}

export interface ClauseVersion {
  id: string;
  versionNumber: number;
  status: ClauseVersionStatus;
  contentSfdt?: string | null;
  contentText: string;
  placeholders: string[];
  changeSummary?: string | null;
  approverIds: string[];
  approvers: PersonRef[];
  submittedBy?: string | null;
  submittedByName?: string | null;
  submittedAt?: string | null;
  decidedByName?: string | null;
  decidedAt?: string | null;
  decisionComment?: string | null;
  publishedAt?: string | null;
  createdByName?: string | null;
  creationDate: string;
  alternates: ClauseAlternate[];
}

export interface ClauseSummary {
  id: string;
  clauseNumber: string;
  title: string;
  /** Master-data clause type (Admin → Clause Types). */
  clauseTypeId: string;
  /** That clause type's name, stored with the clause for display. */
  clauseType: string;
  contractCategoryId?: string | null;
  contractCategoryName?: string | null;
  tags: string[];
  /** Master-data jurisdiction (Admin → Jurisdictions); optional. */
  jurisdictionId?: string | null;
  /** That jurisdiction's name, for display. */
  jurisdiction?: string | null;
  riskLevel: ClauseRiskLevel;
  status: ClauseStatus;
  publishedVersionNumber?: number | null;
  latestVersionNumber: number;
  /** A draft or in-review version exists beside the published one. */
  hasPendingVersion: boolean;
  pendingVersionStatus?: ClauseVersionStatus | null;
  ownerId: string;
  ownerName: string;
  usageCount: number;
  lastUsedAt?: string | null;
  alternateCount: number;
  createdBy: string;
  createdByName?: string | null;
  creationDate: string;
  modifiedBy?: string | null;
  modifiedByName?: string | null;
  modificationDate?: string | null;
  isDeleted: boolean;
  deletedByName?: string | null;
  deletionDate?: string | null;
  deletionReason?: string | null;
  retiredReason?: string | null;
  /** Concurrency token: send back as expectedVersion on every change. */
  version: number;
  /** First 240 characters of the published (else working) wording. */
  excerpt?: string | null;
}

export interface ClauseCapabilities {
  canEdit: boolean;
  canEditContent: boolean;
  canSubmit: boolean;
  canWithdraw: boolean;
  canApprove: boolean;
  canRequestChanges: boolean;
  canRevise: boolean;
  canRetire: boolean;
  canReinstate: boolean;
  canRemove: boolean;
}

export interface ClauseDetail extends ClauseSummary {
  description?: string | null;
  usageGuidance?: string | null;
  publishedVersion?: ClauseVersion | null;
  /** The version being written or reviewed (Draft, InReview or ChangesRequested). */
  workingVersion?: ClauseVersion | null;
  capabilities: ClauseCapabilities;
}

export interface ClauseSearchRequest {
  pageIndex: number;
  pageSize: number;
  searchText?: string;
  statuses?: ClauseStatus[];
  clauseTypeIds?: string[];
  contractCategoryIds?: string[];
  tags?: string[];
  /** Master-data jurisdiction ids. */
  jurisdictionIds?: string[];
  riskLevels?: ClauseRiskLevel[];
  ownerIds?: string[];
  createdBy?: string[];
  /** Only clauses with a published version that is not retired: what may be inserted. */
  publishedOnly?: boolean;
  includeDeleted?: boolean;
  includeContent?: boolean;
  orderBy?:
    | "modificationdate"
    | "newest"
    | "title"
    | "usagecount"
    | "clausenumber"
    | "clausetype"
    | "risk"
    | "status";
  sortAscending?: boolean;
}

export interface FacetCount {
  key: string;
  count: number;
}

/** Clause type facet: key is the clause type id, label its name. */
export interface ClauseTypeFacetCount extends FacetCount {
  label: string;
}

/** Jurisdiction facet: key is the jurisdiction id, label its name. */
export interface JurisdictionFacetCount extends FacetCount {
  label: string;
}

export interface ClauseSearchResponse {
  count: number;
  pageIndex: number;
  pageSize: number;
  data: ClauseSummary[];
  facets?: {
    clauseTypes: ClauseTypeFacetCount[];
    tags: FacetCount[];
    jurisdictions: JurisdictionFacetCount[];
  };
}

export interface ClauseContentInput {
  contentSfdt: string;
  contentText: string;
  placeholders: string[];
  changeSummary?: string | null;
  alternates: ClauseAlternate[];
}

export interface ClauseMetadataInput {
  title: string;
  /** Required on create; on edit, left out = unchanged. The server resolves the name. */
  clauseTypeId: string;
  contractCategoryId?: string | null;
  contractCategoryName?: string | null;
  tags: string[];
  /** Optional master-data jurisdiction; the server resolves the name. null/omitted = none (on create). */
  jurisdictionId?: string | null;
  riskLevel: ClauseRiskLevel;
  description?: string | null;
  usageGuidance?: string | null;
  ownerId?: string | null;
}

export type ClauseCreateRequest = ClauseMetadataInput & ClauseContentInput;

export interface ClauseEditRequest extends Partial<ClauseMetadataInput> {
  id: string;
  expectedVersion: number;
  /** Edit leaves the jurisdiction unchanged when jurisdictionId is omitted; this removes it. */
  clearJurisdiction?: boolean;
  content?: ClauseContentInput;
}

export interface ClauseApprovalEntry {
  round: number;
  versionNumber: number;
  approverName: string;
  decision: ClauseApprovalDecision;
  comment?: string | null;
  decidedAt: string;
}

export interface ClauseUsageEntry {
  id: string;
  targetType: ClauseUsageTarget;
  targetId?: string | null;
  targetLabel: string;
  versionNumber: number;
  position: ClausePosition;
  insertedByName: string;
  insertedAt: string;
}

export interface ClauseAuditRow {
  srNo: number;
  event: string;
  summary: string;
  actorId: string;
  actorName: string;
  occurredAt: string;
  versionNumber?: number | null;
  comment?: string | null;
  changes: Array<{ field: string; from?: string | null; to?: string | null }>;
}

export interface ClauseMatch {
  clauseId: string;
  clauseNumber: string;
  title: string;
  clauseTypeId: string;
  clauseType: string;
  versionId: string;
  versionNumber: number;
  /** 0..1 word-overlap similarity. */
  score: number;
  matchedPosition: ClausePosition;
  riskLevel: ClauseRiskLevel;
}

/** Clause types and jurisdictions come from master data (clauseTypeApi / jurisdictionApi), not from here. */
export interface ClauseLibraryMeta {
  tags: string[];
}

export interface RecordClauseUsageRequest {
  clauseId: string;
  versionId: string;
  position: ClausePosition;
  targetType: ClauseUsageTarget;
  targetId?: string | null;
  targetLabel: string;
}

/** AI comparison of a contract's clause against a library clause. */
export interface AiClauseCompareRequest {
  contractText: string;
  library: {
    clauseId: string;
    title: string;
    clauseType: string;
    preferredText: string;
    alternates: Array<{
      position: "fallback" | "walk_away";
      title: string;
      text: string;
      guidance?: string | null;
    }>;
    usageGuidance?: string | null;
  };
}

export interface AiClauseDeviation {
  topic: string;
  contractSays: string;
  librarySays: string;
  severity: "low" | "medium" | "high";
  recommendation: string;
}

export interface AiClauseCompareResult {
  matchedPosition: "preferred" | "fallback" | "walk_away" | "none";
  similaritySummary: string;
  deviations: AiClauseDeviation[];
  riskLevel: "low" | "medium" | "high";
  suggestedText: string;
  notice?: string | null;
}
